import {
  Alert,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  message,
  Radio,
  Space,
  Spin,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { adminCommerceApi } from '@/services/adminCommerceApi';
import type {
  AdminCommerceRefundPreview,
  AdminCommerceRefundStatus,
  AdminCommerceRefundStrategy,
} from '@/types';

interface RefundDialogProps {
  open: boolean;
  brandId: string;
  orderNo: string;
  existingRefund?: AdminCommerceRefundStatus;
  initialPreview: AdminCommerceRefundPreview | null;
  onClose: () => void;
  onCompleted: () => void;
}

const terminalStatuses = new Set(['succeeded', 'failed', 'manual_review']);

function money(amount: number, currency: string) {
  const exponent = currencyExponent(currency);
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount / 10 ** exponent);
}

function credits(value: number) {
  return new Intl.NumberFormat('zh-CN').format(value);
}

function wait(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function currencyExponent(currency: string) {
  const normalized = currency.toUpperCase();
  if (['JPY', 'KRW'].includes(normalized)) {
    return 0;
  }
  if (['BHD', 'JOD', 'KWD'].includes(normalized)) {
    return 3;
  }
  return 2;
}

function minorFromMajor(value: string | null, currency: string) {
  if (!value || !/^\d+(?:\.\d+)?$/.test(value)) {
    return 0;
  }
  const exponent = currencyExponent(currency);
  const [whole, rawFraction = ''] = value.split('.');
  const fraction = rawFraction.padEnd(exponent + 1, '0');
  const scale = 10 ** exponent;
  let minor =
    Number(whole) * scale +
    Number((fraction.slice(0, exponent) || '0').padEnd(exponent, '0'));
  if (fraction[exponent] >= '5') {
    minor += 1;
  }
  return Number.isSafeInteger(minor) ? minor : 0;
}

function newRefundRequestNo() {
  const suffix =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().split('-').join('')
      : `${Date.now()}${Math.random().toString(16).slice(2)}`;
  return `refund_${suffix}`;
}

export function RefundDialog({
  open,
  brandId,
  orderNo,
  existingRefund,
  initialPreview,
  onClose,
  onCompleted,
}: RefundDialogProps) {
  const [strategy, setStrategy] =
    useState<AdminCommerceRefundStrategy>('suggested');
  const [customAmount, setCustomAmount] = useState<string | null>(null);
  const [applicationReceivedAt, setApplicationReceivedAt] =
    useState<Dayjs | null>(dayjs());
  const [reason, setReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [preview, setPreview] = useState<AdminCommerceRefundPreview | null>(
    initialPreview,
  );
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refundStatus, setRefundStatus] =
    useState<AdminCommerceRefundStatus | null>(null);
  const requestTokenRef = useRef(0);
  const refundRequestNoRef = useRef(newRefundRequestNo());

  useEffect(() => {
    if (!open) {
      requestTokenRef.current += 1;
      return;
    }
    setStrategy(existingRefund?.refundStrategy || 'suggested');
    setCustomAmount(
      existingRefund?.refundStrategy === 'custom'
        ? String(
            existingRefund.approvedRefundAmount /
              10 ** currencyExponent(existingRefund.currency),
          )
        : null,
    );
    setApplicationReceivedAt(
      existingRefund?.applicationReceivedAt
        ? dayjs(existingRefund.applicationReceivedAt)
        : dayjs(),
    );
    setReason(existingRefund?.reason || '');
    setOverrideReason(existingRefund?.overrideReason || '');
    setPreview(initialPreview);
    setRefundStatus(null);
    refundRequestNoRef.current =
      existingRefund?.refundRequestNo || newRefundRequestNo();
  }, [open, initialPreview, existingRefund]);

  const loadPreview = async (
    nextStrategy: AdminCommerceRefundStrategy,
    amountInMinorUnit = 0,
  ) => {
    setPreviewing(true);
    try {
      const result = await adminCommerceApi.previewRefund(brandId, orderNo, {
        refundStrategy: nextStrategy,
        approvedRefundAmount: amountInMinorUnit,
      });
      setPreview(result);
      return result;
    } catch {
      message.error('退款金额计算失败');
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: retry preview is refreshed once for the persisted request.
  useEffect(() => {
    if (!open || !existingRefund?.retryable) {
      return;
    }
    void loadPreview(
      existingRefund.refundStrategy,
      existingRefund.approvedRefundAmount,
    );
  }, [open, existingRefund]);

  const selectStrategy = (nextStrategy: AdminCommerceRefundStrategy) => {
    setStrategy(nextStrategy);
    setRefundStatus(null);
    if (nextStrategy !== 'custom') {
      void loadPreview(nextStrategy);
    } else if (minorFromMajor(customAmount, preview?.currency || 'USD') > 0) {
      void loadPreview(
        'custom',
        minorFromMajor(customAmount, preview?.currency || 'USD'),
      );
    }
  };

  const pollRefund = async (
    requestNo: string,
    token: number,
  ): Promise<AdminCommerceRefundStatus | null> => {
    const startedAt = Date.now();
    while (
      requestTokenRef.current === token &&
      Date.now() - startedAt < 60_000
    ) {
      const elapsed = Date.now() - startedAt;
      await wait(elapsed < 30_000 ? 1000 : 2000);
      if (requestTokenRef.current !== token) {
        return null;
      }
      let status: AdminCommerceRefundStatus;
      try {
        status = await adminCommerceApi.getRefund(brandId, orderNo, requestNo);
      } catch {
        continue;
      }
      setRefundStatus(status);
      if (terminalStatuses.has(status.status)) {
        return status;
      }
    }
    return null;
  };

  const submit = async () => {
    const approvedAmount =
      strategy === 'custom'
        ? minorFromMajor(customAmount, preview?.currency || 'USD')
        : (preview?.approvedRefundAmount ?? 0);
    if (!applicationReceivedAt || !reason.trim()) {
      message.error('请填写完整申请时间和退款原因');
      return;
    }
    if (approvedAmount <= 0) {
      message.error('退款金额必须大于 0');
      return;
    }
    if (
      preview &&
      approvedAmount !== preview.suggestedRefundAmount &&
      !overrideReason.trim()
    ) {
      message.error('退款金额与系统建议不同时必须填写调整原因');
      return;
    }
    setSubmitting(true);
    const token = requestTokenRef.current + 1;
    requestTokenRef.current = token;
    try {
      const latestPreview = await loadPreview(strategy, approvedAmount);
      if (!latestPreview?.refundable) {
        message.error(
          latestPreview?.nonRefundableReason || '该订单当前不可退款',
        );
        return;
      }
      const created = await adminCommerceApi.createRefund(brandId, orderNo, {
        refundRequestNo: refundRequestNoRef.current,
        refundStrategy: strategy,
        approvedRefundAmount: latestPreview.approvedRefundAmount,
        applicationReceivedAt: applicationReceivedAt.toDate().toISOString(),
        reason: reason.trim(),
        overrideReason: overrideReason.trim() || undefined,
      });
      setRefundStatus(created);
      if (created.status === 'failed') {
        message.error(created.message || '退款失败');
        return;
      }
      const finalStatus = await pollRefund(created.refundRequestNo, token);
      if (!finalStatus) {
        message.info('退款仍在处理中，可稍后重新打开订单查看结果');
        onCompleted();
        return;
      }
      if (finalStatus.status === 'succeeded') {
        message.success('退款、积分和会员处理已全部完成');
        onCompleted();
        onClose();
        return;
      }
      if (finalStatus.status === 'manual_review') {
        message.error('Stripe 退款已完成，但权益处理异常，请联系技术人员');
      } else {
        message.error(finalStatus.message || '退款失败');
      }
      onCompleted();
    } catch {
      message.error('退款请求失败，请查看订单状态后再操作');
    } finally {
      if (requestTokenRef.current === token) {
        setSubmitting(false);
      }
    }
  };

  const approvedAmount = preview?.approvedRefundAmount ?? 0;

  return (
    <Modal
      title="订单退款"
      open={open}
      width={760}
      onCancel={submitting ? undefined : onClose}
      onOk={() => void submit()}
      okText={submitting ? '正在等待 Stripe 和权益处理' : '确认退款'}
      cancelButtonProps={{ disabled: submitting }}
      okButtonProps={{
        danger: true,
        loading: submitting,
        disabled: !preview?.refundable && strategy !== 'custom',
      }}
      maskClosable={!submitting}
      closable={!submitting}
    >
      <Spin spinning={previewing}>
        <Space direction="vertical" size="middle" className="w-full">
          <Alert
            type="warning"
            showIcon
            message="退款会影响该订单积分及会员权益"
            description="当前会员来源订单退款会立即终止会员并取消对应 Stripe Subscription；历史订单退款不会终止后来续费或重新购买的新会员。"
          />

          <Radio.Group
            value={strategy}
            onChange={(event) =>
              selectStrategy(event.target.value as AdminCommerceRefundStrategy)
            }
          >
            <Radio.Button value="suggested">系统建议</Radio.Button>
            <Radio.Button value="full">原订单全额</Radio.Button>
            <Radio.Button value="custom">自定义金额</Radio.Button>
          </Radio.Group>

          {strategy === 'custom' && (
            <InputNumber
              className="w-full"
              min="0"
              precision={currencyExponent(preview?.currency || 'USD')}
              stringMode
              value={customAmount}
              addonBefore={preview?.currency?.toUpperCase() || 'USD'}
              placeholder="输入退款金额"
              onChange={(value) =>
                setCustomAmount(typeof value === 'string' ? value : null)
              }
              onBlur={() => {
                const amount = minorFromMajor(
                  customAmount,
                  preview?.currency || 'USD',
                );
                if (amount > 0) {
                  void loadPreview('custom', amount);
                }
              }}
            />
          )}

          {preview && (
            <>
              {!preview.refundable && (
                <Alert
                  type="error"
                  showIcon
                  message={preview.nonRefundableReason || '该订单不可退款'}
                />
              )}
              {preview.warning && (
                <Alert type="warning" showIcon message={preview.warning} />
              )}
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="历史实付">
                  {money(preview.paidAmount, preview.currency)}
                </Descriptions.Item>
                <Descriptions.Item label="最大可退">
                  {money(preview.maxRefundAmount, preview.currency)}
                </Descriptions.Item>
                <Descriptions.Item label="系统建议">
                  {money(preview.suggestedRefundAmount, preview.currency)}
                </Descriptions.Item>
                <Descriptions.Item label="最终退款">
                  {money(approvedAmount, preview.currency)}
                </Descriptions.Item>
                <Descriptions.Item label="订单总积分">
                  {credits(preview.totalEntitlementCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="本单已发积分">
                  {credits(preview.issuedCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="历史积分价值">
                  {preview.totalEntitlementCredits > 0
                    ? `${money(
                        Math.round(
                          (preview.paidAmount * 1000) /
                            preview.totalEntitlementCredits,
                        ),
                        preview.currency,
                      )} / 1,000 积分`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="当前可退积分">
                  {credits(preview.refundableCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="退款对应积分">
                  {credits(preview.refundEquivalentCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="实际回收当前积分">
                  {credits(preview.reclaimCurrentCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="预计无法回收积分">
                  {credits(preview.expectedUnrecoverableCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="取消未来积分">
                  {credits(preview.cancelFutureCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="已消费 / 已过期">
                  {credits(preview.usedCredits)} /{' '}
                  {credits(preview.expiredCredits)}
                </Descriptions.Item>
                <Descriptions.Item label="影响当前会员">
                  {preview.affectsCurrentMembership ? '是，立即终止' : '否'}
                </Descriptions.Item>
                <Descriptions.Item label="额外失效未来积分">
                  {credits(preview.membershipTerminationExtraFutureCredits)}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}

          <div>
            <div className="mb-1 text-sm font-medium">首次收到完整申请时间</div>
            <DatePicker
              showTime
              className="w-full"
              value={applicationReceivedAt}
              onChange={setApplicationReceivedAt}
            />
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">退款原因</div>
            <Input.TextArea
              value={reason}
              rows={2}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          {preview &&
            (strategy !== 'suggested' ||
              preview.approvedRefundAmount !==
                preview.suggestedRefundAmount) && (
              <div>
                <div className="mb-1 text-sm font-medium">金额调整原因</div>
                <Input.TextArea
                  value={overrideReason}
                  rows={2}
                  onChange={(event) => setOverrideReason(event.target.value)}
                />
              </div>
            )}

          {submitting && (
            <Alert
              type="info"
              showIcon
              message="正在等待退款完成"
              description={`Stripe：${refundStatus?.stripeRefundStatus || '处理中'}；本地阶段：${refundStatus?.localProcessingStage || '等待 Webhook'}`}
            />
          )}
        </Space>
      </Spin>
    </Modal>
  );
}
